import { useEffect } from "react";
import {
  ArrowForward,
  AutoAwesomeOutlined,
  InsightsOutlined,
  MedicalServicesOutlined,
  PersonPinOutlined,
  SvgIconComponent,
} from "@mui/icons-material";
import { Box, CircularProgress, CssBaseline, ThemeProvider, Typography } from "@mui/material";
import { TopBar } from "../components";
import Roles from "../components/Authorization/Roles";
import { blue, grey, indigo } from "../colors";
import { Feature } from "../featureFlags/featureFlagConfig";
import useFeatureFlag from "../featureFlags/useFeatureFlag";
import { theme } from "../theme";
import { TOP_BAR_HEIGHT } from "./AppLayout";

type RoleSelectorProps = {
  setUsersDomainRole: (domainRole: string, urlRedirect?: string) => void;
  userRoles: string[];
};

type DomainCard = {
  name: string;
  role: string;
  icon: SvgIconComponent;
  desc: string;
  urlRedirect?: string;
};

/**
 * Domain selection (Welcome) page.
 *
 * Users with a single domain skip this page entirely.
 * Users with multiple AD groups land here to make a clear Medical / Commercial / PMR
 * choice before entering the app.
 */
const RoleSelectorPage = ({
  setUsersDomainRole,
  userRoles,
  isLoading,
}: RoleSelectorProps & { isLoading: boolean }): JSX.Element => {
  const pmr_feature = useFeatureFlag(Feature.PrimaryMarketResearch);
  const retros_feature = useFeatureFlag(Feature.Retros);

  const DOMAIN_CARDS: DomainCard[] = [
    {
      name: "Commercial",
      role: Roles.COMMERCIAL,
      icon: AutoAwesomeOutlined,
      urlRedirect: "/explorer",
      desc: "Chat with commercial Veeva and VOC sources, explore trending topics, and build dashboards — all grounded in commercial data.",
    },
    {
      name: "Medical",
      role: Roles.MEDICAL,
      icon: MedicalServicesOutlined,
      urlRedirect: "/explorer",
      desc: "Chat with medical Veeva, Synapse, VOC and Twitter sources, explore trending topics, and build dashboards — all grounded in medical data.",
    },
    ...(pmr_feature.enabled
      ? [
          {
            name: "Primary Market Research",
            role: Roles.PRIMARY_MARKET_RESEARCH,
            icon: PersonPinOutlined,
            urlRedirect: "/market-research",
            desc: "Ask questions and generate summaries across personas, specialties, practice types, and target priorities to understand key market trends.",
          },
        ]
      : []),
    ...(retros_feature.enabled
      ? [
          {
            name: "Retros",
            role: Roles.USLT_RETRO,
            icon: InsightsOutlined,
            urlRedirect: "/dashboards/retros",
            desc: "Review quarterly retrospective summaries and portfolio insights across therapeutic areas.",
          },
        ]
      : []),
  ];

  useEffect(() => {
    sessionStorage.removeItem("appFilterState");
  }, []);

  const availableCards = DOMAIN_CARDS.filter((card) => userRoles.includes(card.role));

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, backgroundColor: grey[50], minHeight: "100vh" }}>
        <CssBaseline />
        <TopBar hideSearchBar showWelcome={false} />
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{
            flex: 1,
            px: 3,
            marginTop: `${TOP_BAR_HEIGHT}px`,
            pt: 10,
            pb: 6,
          }}
        >
          <Typography
            sx={{
              fontSize: "40px",
              fontWeight: 700,
              textAlign: "center",
              lineHeight: 1.15,
              color: indigo[700],
            }}
          >
            Welcome to Deepsense
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontSize: "16px",
              fontWeight: 400,
              color: grey[600],
              textAlign: "center",
              maxWidth: "32rem",
            }}
          >
            Choose a workspace to get started.
          </Typography>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                mt: 6,
                display: "grid",
                gap: 3,
                width: "100%",
                maxWidth: availableCards.length > 2 ? "60rem" : "44rem",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: availableCards.length > 1 ? "repeat(2, 1fr)" : "1fr",
                  md: `repeat(${Math.min(availableCards.length, 3)}, 1fr)`,
                },
              }}
            >
              {availableCards.map((card) => (
                <DomainSelectCard
                  key={card.role}
                  card={card}
                  onSelect={() => setUsersDomainRole(card.role, card.urlRedirect)}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

const DomainSelectCard = ({ card, onSelect }: { card: DomainCard; onSelect: () => void }) => {
  const Icon = card.icon;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "200px",
        p: 3,
        cursor: "pointer",
        backgroundColor: "#FFFFFF",
        border: `1px solid ${grey[100]}`,
        borderRadius: "32px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05), 0 1px 4px rgba(0, 0, 0, 0.05)",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        outline: "none",
        "&:hover, &:focus-visible": {
          borderColor: blue[100],
          backgroundColor: "#fbfbfb",
          transform: "translateY(-2px)",
        },
        "&:hover .domain-arrow, &:focus-visible .domain-arrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 48,
          height: 48,
          borderRadius: "12px",
          backgroundColor: indigo[100],
          color: indigo[900],
          mb: 2,
        }}
      >
        <Icon sx={{ fontSize: 26 }} />
      </Box>

      <Typography sx={{ fontSize: "20px", fontWeight: 700, color: indigo[900], mb: 1 }}>{card.name}</Typography>
      <Typography sx={{ fontSize: "14px", fontWeight: 400, color: grey[600], lineHeight: 1.5, flexGrow: 1 }}>
        {card.desc}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", mt: 2.5 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: blue[700] }}>Enter</Typography>
        <ArrowForward
          className="domain-arrow"
          sx={{
            fontSize: 18,
            ml: 0.5,
            color: blue[700],
            opacity: 0,
            transform: "translateX(-4px)",
            transition: "all 0.18s ease",
          }}
        />
      </Box>
    </Box>
  );
};

export default RoleSelectorPage;
